import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { existsSync } from 'node:fs'
import { homedir } from 'node:os'
import { dirname, join, resolve } from 'node:path'

const execFileAsync = promisify(execFile)

/**
 * Returns the npm package root directory.
 * Computed as one level up from __dirname since compiled .cjs output lives in dist/.
 * Throws if the vendored jCodeMunch server.py is not found at the expected path.
 */
export function getPackageRoot(): string {
  const root = resolve(dirname(__filename), '..')
  const serverPath = join(root, 'python', 'jcodemunch_mcp', 'server.py')
  if (!existsSync(serverPath)) {
    throw new Error(
      `jCodeMunch vendored source not found at ${serverPath} -- reinstall gsd-munch`,
    )
  }
  return root
}

/**
 * Returns the absolute path to the vendored jCodeMunch server.py entry point.
 */
export function getServerPath(): string {
  return join(getPackageRoot(), 'python', 'jcodemunch_mcp', 'server.py')
}

/**
 * Returns the python/ directory path, used as PYTHONPATH when spawning the MCP server.
 */
export function getPythonPath(): string {
  return join(getPackageRoot(), 'python')
}

/**
 * Returns the fixed venv location at ~/.local/share/gsd-munch/venv.
 */
export function getVenvPath(): string {
  return join(homedir(), '.local', 'share', 'gsd-munch', 'venv')
}

/**
 * Returns the absolute path to the venv Python binary.
 * Checks bin/python3 then bin/python on Unix; Scripts/python3.exe then Scripts/python.exe on Windows.
 * Falls back to bin/python (unix) or Scripts/python.exe (windows) if neither exists.
 */
export function getVenvPythonPath(): string {
  const venvPath = getVenvPath()

  if (process.platform === 'win32') {
    const python3 = join(venvPath, 'Scripts', 'python3.exe')
    const python = join(venvPath, 'Scripts', 'python.exe')
    if (existsSync(python3)) return python3
    if (existsSync(python)) return python
    return python
  }

  const python3 = join(venvPath, 'bin', 'python3')
  const python = join(venvPath, 'bin', 'python')
  if (existsSync(python3)) return python3
  if (existsSync(python)) return python
  return python
}

/**
 * Creates the venv at the fixed location if it does not already exist.
 * Uses uv venv with Python 3.10.
 *
 * @param uvPath Absolute path to the uv binary
 */
export async function ensureVenv(uvPath: string): Promise<void> {
  const venvPath = getVenvPath()
  await execFileAsync(uvPath, ['venv', '--python', '3.10', '--allow-existing', venvPath], {
    timeout: 60_000,
  })
}

/**
 * Installs jCodeMunch Python dependencies into the venv using uv pip install.
 * Reads from python/requirements.txt in the package root.
 * Uses a 180s timeout to allow for large wheel downloads (tree-sitter-language-pack).
 *
 * @param uvPath Absolute path to the uv binary
 */
export async function installJcodemunchDeps(uvPath: string): Promise<void> {
  const venvPath = getVenvPath()
  const requirementsPath = join(getPackageRoot(), 'python', 'requirements.txt')
  await execFileAsync(
    uvPath,
    ['pip', 'install', '--python', venvPath, '-r', requirementsPath],
    { timeout: 180_000 },
  )
}
